// In-app update prompt for the GoOil DMS Android app.
// Shows only inside the Capacitor app AND only when the @capacitor/app plugin exists
// (app 1.1 / versionCode 2 onwards). Older APKs do not have that plugin, so for them
// this renders nothing and never throws. In a normal browser it also renders nothing.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Download } from "lucide-react";

const RECHECK_MS = 30 * 60 * 1000; // check again on resume, at most every 30 minutes
const DISMISS_KEY = "gooil_update_dismissed"; // versionCode the user said "Later" to

const appPlugin = () => {
  try {
    const C = window.Capacitor;
    if (!C || !C.isNativePlatform || !C.isNativePlatform()) return null;
    if (C.isPluginAvailable && !C.isPluginAvailable("App")) return null;
    const App = C.Plugins && C.Plugins.App;
    return App && typeof App.getInfo === "function" ? App : null;
  } catch (e) {
    return null;
  }
};

const readDismissed = () => {
  try { return parseInt(sessionStorage.getItem(DISMISS_KEY) || "0", 10) || 0; } catch (e) { return 0; }
};

export default function AppUpdatePrompt() {
  const [update, setUpdate] = useState(null); // { versionName, apk_url, notes, current }
  const lastCheckRef = useRef(0);

  const check = useCallback(async () => {
    const App = appPlugin();
    if (!App) return;
    const now = Date.now();
    if (now - lastCheckRef.current < RECHECK_MS) return;
    lastCheckRef.current = now;
    try {
      const info = await App.getInfo();
      const build = parseInt(info && info.build, 10);
      if (!Number.isFinite(build)) return;
      const res = await fetch("/api/app/version", { cache: "no-store" });
      if (!res.ok) return;
      const latest = await res.json();
      const code = parseInt(latest && latest.versionCode, 10) || 0;
      if (code > build && latest.apk_url && readDismissed() < code) {
        setUpdate({
          code,
          versionName: latest.versionName || "",
          apk_url: latest.apk_url,
          notes: latest.notes || "",
          current: (info && info.version) || "",
        });
      }
    } catch (e) {
      // Network or plugin error: try again on the next resume.
      lastCheckRef.current = 0;
    }
  }, []);

  useEffect(() => {
    if (!appPlugin()) return undefined;
    check();
    const onVisible = () => { if (document.visibilityState === "visible") check(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [check]);

  if (!update) return null;

  const later = () => {
    try { sessionStorage.setItem(DISMISS_KEY, String(update.code)); } catch (e) { /* ignore */ }
    setUpdate(null);
  };
  const doUpdate = () => {
    // The app hands this download to the phone's browser, which downloads the APK
    // and offers to install it.
    window.location.href = update.apk_url;
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4" role="dialog" aria-modal="true" aria-labelledby="app-update-title">
      <div className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-2xl">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-black">
            <Download className="h-5 w-5 text-[#d4a017]" />
          </div>
          <div>
            <h2 id="app-update-title" className="text-base font-bold text-slate-900">New version available</h2>
            <p className="text-xs text-slate-500">
              {update.current ? `Installed: ${update.current} · ` : ""}Latest: {update.versionName || update.code}
            </p>
          </div>
        </div>
        {update.notes ? <p className="mt-3 whitespace-pre-line text-sm text-slate-700">{update.notes}</p> : null}
        <p className="mt-3 text-xs text-slate-500">
          Tap Update to download the new app, then open the downloaded file and tap Install.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={later} className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100">
            Later
          </button>
          <button type="button" onClick={doUpdate} className="rounded-lg bg-black px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800">
            Update
          </button>
        </div>
      </div>
    </div>
  );
}
